import { Paper, styled } from "@mui/material";
import {
    GetFieldLookup,
    GetFieldLookupItem,
    useFormMetadata,
    useFormSave,
    useLookups,
    useSnackbar,
    type ILookup,
} from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Contract, FormDefinitions } from "../api/dtos";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn, AppScalarValue } from "../components/tables/AppTable.types";
import TitleBox from "../components/TitleBox";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import useContractsByStageId from "../hooks/data/useContractsByStageId";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

function setContractField<K extends keyof Contract>(row: Contract, key: K, value: Contract[K]): Contract {
    const updated = Object.assign(Object.create(Object.getPrototypeOf(row)) as Contract, row);

    updated[key] = value;

    return updated;
}

function lookupOptions(lookups: ILookup[] | null | undefined, propertyName: string) {
    return (GetFieldLookup(lookups, propertyName)?.items ?? []).flatMap((item) => {
        if (item.id === null || item.id === undefined || item.id === "") {
            return [];
        }

        const value = typeof item.id === "number" ? item.id : Number(item.id);

        return Number.isFinite(value)
            ? [
                  {
                      value,
                      label: item.value?.toString() ?? String(value),
                  },
              ]
            : [];
    });
}

function lookupLabel(lookups: ILookup[] | null | undefined, propertyName: string, value: AppScalarValue): string {
    if (value === null || value === undefined) {
        return "";
    }

    return GetFieldLookupItem(lookups, propertyName, value)?.value?.toString() ?? String(value);
}

export const buildContractColumns = (lookups: ILookup[] | null | undefined): AppColumn<Contract>[] => {
    const lookupColumn = (
        id: "lotId" | "status" | "agentLocationId",
        label: string,
        lookupName: string,
        accessor: (row: Contract) => number | undefined,
    ): AppColumn<Contract> => ({
        id,
        label,
        accessor,
        scalarType: "lookup",
        flex: 1,
        lookup: {
            options: lookupOptions(lookups, lookupName),
            labelFor: (value) => lookupLabel(lookups, lookupName, value),
        },
        sortAccessor: (row) => lookupLabel(lookups, lookupName, accessor(row)),
        sortScalarType: "text",
    });
    const dateColumn = (
        id: "issueDate" | "exchangeDate" | "expectedSettleDate" | "settleDate",
        label: string,
    ): AppColumn<Contract> => ({
        id,
        label,
        accessor: (row) => contractLocalDateTime(row[id]),
        scalarType: "date",
        dateSerialization: "localDateTime",
        flex: 1,
        formatter: (value) =>
            typeof value === "string" && value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "",
        setValue: (row, value) => setContractField(row, id, typeof value === "string" ? value : undefined),
    });

    return [
        lookupColumn("lotId", "Lot Number", "LotId", (row) => row.lotId),
        lookupColumn("status", "Contract Status", "Status", (row) => row.status),
        lookupColumn("agentLocationId", "Agent", "AgentLocationId", (row) => row.agentLocationId),
        {
            id: "price",
            label: "Price",
            accessor: (row) => row.price,
            scalarType: "number",
            flex: 1,
            setValue: (row, value) => setContractField(row, "price", typeof value === "number" ? value : undefined),
        },
        dateColumn("issueDate", "Issue Date"),
        dateColumn("exchangeDate", "Exchange Date"),
        dateColumn("expectedSettleDate", "Expected Settle Date"),
        dateColumn("settleDate", "Settle Date"),
    ];
};

function contractLocalDateTime(value: string | undefined): string | undefined {
    if (!value) {
        return value;
    }

    const parsed = DateTime.fromISO(value);

    return parsed.isValid
        ? (parsed.toISO({
              includeOffset: false,
          }) ?? value)
        : value;
}

function errorMessages(reason: unknown): string[] {
    if (typeof reason !== "object" || reason === null) {
        return ["Unable to save contract."];
    }

    const responseStatus = (reason as { responseStatus?: { errors?: readonly { message?: string }[] } }).responseStatus;
    const messages = responseStatus?.errors
        ?.map((error) => error.message)
        .filter((message): message is string => Boolean(message));

    return messages?.length ? messages : ["Unable to save contract."];
}

export const BulkEditContracts: FC = () => {
    const contractMetadata = useFormMetadata(FormDefinitions.Contract);
    const save = useFormSave(contractMetadata);
    const { data: lookups } = useLookups(contractMetadata);
    const params = useParams();
    const stageId = Number(params.stageId);
    const { data: project } = useContext(ProjectContext);
    const { data: stage } = useContext(StageContext);
    const { data: contracts, loading: contractsLoading, refresh: contractsRefresh } = useContractsByStageId(stageId);
    const snackbar = useSnackbar();

    const submit = async (draft: Contract, _original: Contract) => {
        try {
            const persisted = (await save(draft)) as Contract | undefined;

            await contractsRefresh();

            return persisted ?? draft;
        } catch (reason) {
            for (const message of errorMessages(reason))
                snackbar.enqueueSnackbar(message, {
                    variant: "error-critical",
                });
            throw reason;
        }
    };

    const columns = useMemo(() => buildContractColumns(lookups), [lookups]);
    const exportTitle = `${project?.name} - ${stage?.name} - Contracts`;

    return (
        <>
            <Outlet />
            <TitleBox />
            <StyledPaper>
                <CollapsibleAppTable
                    density="compact"
                    startCollapsed={false}
                    title="Contracts"
                    loading={contractsLoading}
                    columns={columns}
                    rows={contracts ?? []}
                    getRowId={(contract) => {
                        if (contract.id == null) {
                            throw new Error("Contract is missing its domain ID");
                        }

                        return contract.id;
                    }}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    groupingEnabled={false}
                    editing={{
                        mode: "row",
                        onCommit: submit,
                    }}
                    export={{
                        fileName: exportTitle,
                        exportTitle,
                        includeDateTime: true,
                    }}
                />
            </StyledPaper>
        </>
    );
};

export default BulkEditContracts;
